import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useDelai, useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { BarreRecherche } from '../components/BarreRecherche';
import { IconeCivilite } from '../components/IconeCivilite';
import { TagEntite } from '../components/TagEntite';
import type { Contrat, Lieu, Personnel } from '../types';
import { ChampTelephone } from '../components/ChampTelephone';
import { formaterTelephone, telephonePourSaisie } from '../lib/telephone';

/** Statuts d'emploi proposes a la saisie, dans l'ordre du tableau des salaires. */






/**
 * Lieux et personnel sont des referentiels PARTAGES : ils sont
 * administres une seule fois pour toutes les entites. Le bandeau le
 * rappelle, sans quoi on croit modifier « ses » lieux.
 */
function BandeauPartage() {
  return (
    <p className="mb-4 rounded-md border border-encre-200 bg-white p-3 text-xs text-encre-500">
      Referentiel partage : une modification vaut pour toutes les entites.
    </p>
  );
}

// --- Lieux ------------------------------------------------------------

export function LieuxPage() {
  const { peut } = useAuth();
  const [inclureInactifs, setInclureInactifs] = useState(false);
  const [modale, setModale] = useState<{ ouverte: boolean; lieu: Lieu | null }>({
    ouverte: false,
    lieu: null,
  });

  const { donnees, chargement, erreur, recharger } = useRessource(
    () => api.get<Lieu[]>('/api/lieux', { inclureInactifs }),
    [inclureInactifs],
  );

  const colonnes: Colonne<Lieu>[] = [
    {
      cle: 'code',
      entete: 'Code',
      rendu: (l) => <span className="font-mono text-xs font-semibold">{l.code}</span>,
    },
    {
      cle: 'libelle',
      entete: 'Libelle',
      rendu: (l) => <span className="font-medium">{l.libelle}</span>,
    },
    {
      cle: 'adresse',
      entete: 'Adresse',
      rendu: (l) => (
        <span className="text-encre-500">
          {[l.adresse, l.code_postal, l.ville].filter(Boolean).join(', ') || '—'}
        </span>
      ),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (l) => (
        <Badge ton={l.actif ? 'succes' : 'neutre'}>{l.actif ? 'Actif' : 'Desactive'}</Badge>
      ),
    },
    {
      cle: 'actions',
      entete: '',
      classe: 'text-right',
      rendu: (l) =>
        peut('lieu.desactiver') ? (
          <button
            type="button"
            className="text-sm text-encre-500 underline hover:text-encre-900"
            onClick={async (evenement) => {
              evenement.stopPropagation();
              await api.patch(`/api/lieux/${l.id}/activation`, { actif: !l.actif });
              recharger();
            }}
          >
            {l.actif ? 'Desactiver' : 'Reactiver'}
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <EnTetePage
        titre="Lieux"
        description="Salles et sites ou se deroulent les cours."
        actions={
          peut('lieu.creer') ? (
            <button
              type="button"
              className="bouton-principal"
              onClick={() => setModale({ ouverte: true, lieu: null })}
            >
              Nouveau lieu
            </button>
          ) : null
        }
      />

      <BandeauPartage />

      <label className="mb-4 inline-flex items-center gap-2 text-sm text-encre-600">
        <input
          type="checkbox"
          checked={inclureInactifs}
          onChange={(evenement) => setInclureInactifs(evenement.target.checked)}
        />
        Afficher les lieux desactives
      </label>

      {erreur && <Erreur message={erreur} onReessayer={recharger} />}
      {chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={donnees ?? []}
            cleDe={(l) => l.id}
            messageVide="Aucun lieu."
            onLigneCliquee={
              peut('lieu.modifier') ? (l) => setModale({ ouverte: true, lieu: l }) : undefined
            }
          />
        </div>
      )}

      <FormulaireLieu
        etat={modale}
        onFermer={() => setModale({ ouverte: false, lieu: null })}
        onEnregistre={() => {
          setModale({ ouverte: false, lieu: null });
          recharger();
        }}
      />
    </>
  );
}

function FormulaireLieu({
  etat,
  onFermer,
  onEnregistre,
}: {
  etat: { ouverte: boolean; lieu: Lieu | null };
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    code: '',
    libelle: '',
    adresse: '',
    codePostal: '',
    ville: '',
    salleUnique: false,
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = etat.ouverte ? (etat.lieu?.id ?? 'nouveau') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setValeurs({
      code: etat.lieu?.code ?? '',
      libelle: etat.lieu?.libelle ?? '',
      adresse: etat.lieu?.adresse ?? '',
      codePostal: etat.lieu?.code_postal ?? '',
      ville: etat.lieu?.ville ?? '',
      salleUnique: etat.lieu?.salle_unique ?? false,
    });
    setErreur(null);
  }
  if (!etat.ouverte && initialisePour !== null) setInitialisePour(null);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const corps = {
        libelle: valeurs.libelle,
        adresse: valeurs.adresse || null,
        codePostal: valeurs.codePostal || null,
        ville: valeurs.ville || null,
        salleUnique: valeurs.salleUnique,
      };
      if (etat.lieu) {
        await api.patch(`/api/lieux/${etat.lieu.id}`, corps);
      } else {
        await api.post('/api/lieux', { ...corps, code: valeurs.code });
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={etat.lieu ? 'Modifier le lieu' : 'Nouveau lieu'}
      ouverte={etat.ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        {/* Le code reste visible en modification, grise : il identifie le
            lieu dans le planning et ne se change plus apres creation. */}
        <Champ
          libelle="Code"
          aide={
            etat.lieu
              ? 'Non modifiable après création : c’est lui qui identifie le lieu dans le planning.'
              : 'Code court utilise dans le planning (SM, CDT, ELZ…).'
          }
        >
          <input
            className={`champ font-mono uppercase ${etat.lieu ? 'bg-encre-50 text-encre-600' : ''}`}
            value={valeurs.code}
            onChange={(e) => setValeurs({ ...valeurs, code: e.target.value.toUpperCase() })}
            required
            autoFocus={!etat.lieu}
            readOnly={Boolean(etat.lieu)}
          />
        </Champ>

        <Champ libelle="Libelle">
          <input
            className="champ"
            value={valeurs.libelle}
            onChange={(e) => setValeurs({ ...valeurs, libelle: e.target.value })}
            required
          />
        </Champ>

        <Champ libelle="Adresse">
          <input
            className="champ"
            value={valeurs.adresse}
            onChange={(e) => setValeurs({ ...valeurs, adresse: e.target.value })}
          />
        </Champ>

        <div className="grid grid-cols-3 gap-3">
          <Champ libelle="Code postal">
            <input
              className="champ tabulaire"
              value={valeurs.codePostal}
              onChange={(e) => setValeurs({ ...valeurs, codePostal: e.target.value })}
              inputMode="numeric"
              maxLength={5}
            />
          </Champ>
          <div className="col-span-2">
            <Champ libelle="Ville">
              <input
                className="champ"
                value={valeurs.ville}
                onChange={(e) => setValeurs({ ...valeurs, ville: e.target.value })}
              />
            </Champ>
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={valeurs.salleUnique}
            onChange={(e) => setValeurs({ ...valeurs, salleUnique: e.target.checked })}
          />
          <span>
            Salle unique
            <span className="mt-0.5 block text-xs text-encre-400">
              Cochez si le lieu n’accueille qu’un cours à la fois. Le planning signalera alors tout
              chevauchement. Un site à plusieurs salles doit rester décoché — sinon chaque cours
              individuel parallèle serait compté comme un conflit.
            </span>
          </span>
        </label>

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
      </form>
    </Modale>
  );
}

// --- Equipe (personnel) ------------------------------------------------

export function PersonnelPage() {
  const { peut, entiteActive } = useAuth();
  const naviguer = useNavigate();
  const [modale, setModale] = useState<{ ouverte: boolean; personnel: Personnel | null }>({
    ouverte: false,
    personnel: null,
  });
  const [saisie, setSaisie] = useState('');
  // Meme delai que sur l'ecran Adherents : les listes se comportent pareil.
  const recherche = useDelai(saisie);
  // Par defaut, l'EFFECTIF : le referentiel porte 119 fiches quand une
  // trentaine seulement travaille aujourd'hui. Les fiches sans contrat
  // sont des professeurs partis ou pas encore embauches — on les
  // retrouve en decochant.
  const [avecContrat, setAvecContrat] = useState(true);

  const { donnees, chargement, erreur, recharger } = useRessource(
    () => api.get<Personnel[]>('/api/personnel'),
    [],
  );

  // Les contrats sont des donnees de PAIE : seule la Direction les voit
  // (permission contrat.lire), et la consultation est journalisee cote
  // serveur. L'ecran degrade proprement pour les autres roles.
  //
  // `toutesEntites` : la table est cloisonnee par RLS, et sans cette vue
  // transverse l'ecran ne pourrait pas dire pour quelle ecole travaille
  // chacun. Seules les entites ou l'utilisateur detient `contrat.lire`
  // sont parcourues, cote serveur.
  const peutContrats = peut('contrat.lire');
  const contrats = useRessource(
    () =>
      peutContrats
        ? api.get<Contrat[]>('/api/contrats', { toutesEntites: true })
        : Promise.resolve([]),
    [peutContrats, entiteActive?.id],
  );

  const contratsDe = (personnelId: string) =>
    (contrats.donnees ?? []).filter((c) => c.personnel_id === personnelId && c.en_cours);

  // La recherche porte sur ce qui identifie quelqu'un : prenom, nom,
  // email. Cote client — la liste tient en une charge, comme les filtres
  // d'age de l'ecran Adherents.
  const terme = recherche.trim().toLowerCase();
  const liste = (donnees ?? []).filter((p) => {
    if (avecContrat && peutContrats && contratsDe(p.id).length === 0) return false;
    if (!terme) return true;
    return [p.prenom_usuel, p.nom, p.email]
      .filter(Boolean)
      .some((valeur) => valeur!.toLowerCase().includes(terme));
  });

  const colonnes: Colonne<Personnel>[] = [
    {
      cle: 'prenom',
      entete: 'Prénom',
      rendu: (p) => (
        <span className="flex items-center gap-2 font-medium">
          <IconeCivilite civilite={p.civilite} />
          {p.prenom_usuel}
        </span>
      ),
    },
    { cle: 'nom', entete: 'Nom legal', rendu: (p) => p.nom ?? '—' },
    { cle: 'email', entete: 'Email', rendu: (p) => p.email ?? '—' },
    {
      cle: 'telephone',
      entete: 'Téléphone',
      rendu: (p) => formaterTelephone(p.telephone, p.telephone_pays) || '—',
    },
    {
      // P1 : date d'entree et statut vivent sur le CONTRAT, par entite.
      // Une fiche SANS contrat n'affiche rien : l'absence se lit d'elle
      // meme, et une pastille attirait l'oeil sur un non-evenement.
      cle: 'contrats',
      entete: 'Contrat',
      rendu: (p) => {
        if (!peutContrats) return <span className="text-encre-300">—</span>;
        return (
          // Une ligne par contrat, les unes sous les autres (2026-10-30) :
          // un salarie des deux ecoles se lit d'un coup d'oeil.
          <span className="flex flex-col gap-1">
            {contratsDe(p.id).map((c) => (
              // Deux pastilles distinctes : l'ecole, a sa couleur, et la
              // nature du contrat. Le statut (professeur, animateur) n'y
              // est plus — il se lit sur la fiche detaillee, ou il a la
              // place d'etre juste. Puis l'anciennete du mois en cours et
              // ses points, ceux que l'API calcule (0.71.0).
              <span key={c.id} className="flex items-center gap-1.5">
                <TagEntite code={c.entite_code} couleur={c.entite_couleur} />
                <Badge>{c.type.toUpperCase()}</Badge>
                <span className="whitespace-nowrap text-xs text-encre-500 tabulaire">
                  {c.anciennete_mois} mois · {c.points_anciennete} {c.points_anciennete === 1 ? 'pt' : 'pts'}
                </span>
              </span>
            ))}
          </span>
        );
      },
    },
    // Il n'y a ni colonne de sortie ni action « Desactiver » : la vie
    // d'un personnel se lit dans ses contrats — on sort quelqu'un de
    // l'effectif en cloturant son contrat, pas en touchant sa fiche.
    // Le lien vers les contrats a disparu : la fiche detaillee les porte.
  ];

  return (
    <>
      <EnTetePage
        titre="Gestion de l’équipe pédagogique"
        description="Le personnel des deux écoles — salariés et intervenants. Le prénom est celui du planning : le classeur identifie chacun par prénom seul."
        actions={
          peut('personnel.creer') ? (
            <button
              type="button"
              className="bouton-principal"
              onClick={() => setModale({ ouverte: true, personnel: null })}
            >
              Nouvelle fiche
            </button>
          ) : null
        }
      />

      <BandeauPartage />

      <BarreRecherche
        recherche={saisie}
        onChercher={setSaisie}
        placeholder="Prénom, nom ou email…"
        filtres={
          peutContrats ? (
            <label className="flex items-center gap-2 text-sm text-encre-600">
              <input
                type="checkbox"
                checked={avecContrat}
                onChange={(evenement) => setAvecContrat(evenement.target.checked)}
              />
              Avec contrat en cours
            </label>
          ) : null
        }
        actifs={[
          ...(recherche
            ? [{ cle: 'recherche', libelle: `« ${recherche} »`, onRetirer: () => setSaisie('') }]
            : []),
          ...(avecContrat && peutContrats
            ? [
                {
                  cle: 'contrat',
                  libelle: 'Avec contrat en cours',
                  onRetirer: () => setAvecContrat(false),
                },
              ]
            : []),
        ]}
        onToutEffacer={() => {
          setSaisie('');
          setAvecContrat(false);
        }}
        resume={
          donnees ? (
            <>
              <span className="tabulaire font-medium text-encre-800">{liste.length}</span> fiche(s)
              sur {donnees.length}
            </>
          ) : null
        }
      />

      {erreur && <Erreur message={erreur} onReessayer={recharger} />}
      {chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={liste}
            cleDe={(p) => p.id}
            messageVide="Aucune fiche de personnel."
            onLigneCliquee={(p) => naviguer(`/personnel/${p.id}`)}
          />
        </div>
      )}

      <FormulairePersonnel
        etat={modale}
        onFermer={() => setModale({ ouverte: false, personnel: null })}
        onEnregistre={() => {
          setModale({ ouverte: false, personnel: null });
          recharger();
        }}
      />
    </>
  );
}

export function FormulairePersonnel({
  etat,
  onFermer,
  onEnregistre,
}: {
  etat: { ouverte: boolean; personnel: Personnel | null };
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  // L'etat civil et le paiement (NIR, IBAN...) sont des donnees de paie :
  // seule la Direction (contrat.gerer) les voit et les ecrit. Le serveur
  // refuse ces champs aux autres — on ne les envoie donc pas.
  const { peut } = useAuth();
  const peutPaie = peut('contrat.gerer');
  const [valeurs, setValeurs] = useState({
    // Defaut « M. » : c'est la consigne de reprise, pas un choix sur la
    // personne — la liste se corrige en un clic.
    civilite: 'M',
    prenomUsuel: '',
    nom: '',
    email: '',
    telephone: '',
    telephonePays: 'FR',
    dateNaissance: '',
    lieuNaissance: '',
    numeroSecuriteSociale: '',
    adresse: '',
    codePostal: '',
    ville: '',
    iban: '',
    bic: '',
    numeroGuso: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = etat.ouverte ? (etat.personnel?.id ?? 'nouveau') : null;
  if (cle && cle !== initialisePour) {
    setInitialisePour(cle);
    setValeurs({
      civilite: etat.personnel?.civilite ?? 'M',
      prenomUsuel: etat.personnel?.prenom_usuel ?? '',
      nom: etat.personnel?.nom ?? '',
      email: etat.personnel?.email ?? '',
      telephone: telephonePourSaisie(etat.personnel?.telephone, etat.personnel?.telephone_pays),
      telephonePays: etat.personnel?.telephone_pays ?? 'FR',
      dateNaissance: etat.personnel?.date_naissance ?? '',
      lieuNaissance: etat.personnel?.lieu_naissance ?? '',
      numeroSecuriteSociale: etat.personnel?.numero_securite_sociale ?? '',
      adresse: etat.personnel?.adresse ?? '',
      codePostal: etat.personnel?.code_postal ?? '',
      ville: etat.personnel?.ville ?? '',
      iban: etat.personnel?.iban ?? '',
      bic: etat.personnel?.bic ?? '',
      numeroGuso: etat.personnel?.numero_guso ?? '',
    });
    setErreur(null);
  }
  if (!etat.ouverte && initialisePour !== null) setInitialisePour(null);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const corps = {
        civilite: valeurs.civilite,
        prenomUsuel: valeurs.prenomUsuel,
        nom: valeurs.nom || null,
        email: valeurs.email || null,
        telephone: valeurs.telephone || null,
        telephonePays: valeurs.telephonePays,
        // Les cles paie ne partent que si l'utilisateur y a droit : leur
        // simple presence declenche le controle serveur.
        ...(peutPaie
          ? {
              dateNaissance: valeurs.dateNaissance || null,
              lieuNaissance: valeurs.lieuNaissance || null,
              numeroSecuriteSociale: valeurs.numeroSecuriteSociale || null,
              adresse: valeurs.adresse || null,
              codePostal: valeurs.codePostal || null,
              ville: valeurs.ville || null,
              iban: valeurs.iban || null,
              bic: valeurs.bic || null,
              numeroGuso: valeurs.numeroGuso || null,
            }
          : {}),
      };
      if (etat.personnel) {
        await api.patch(`/api/personnel/${etat.personnel.id}`, corps);
      } else {
        await api.post('/api/personnel', corps);
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      titre={etat.personnel ? 'Modifier la fiche' : 'Nouveau salarié'}
      ouverte={etat.ouverte}
      onFermer={onFermer}
    >
      <form onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-[7rem_1fr] gap-3">
          <Champ libelle="Civilité">
            <select
              className="champ"
              value={valeurs.civilite}
              onChange={(e) => setValeurs({ ...valeurs, civilite: e.target.value })}
            >
              <option value="M">M.</option>
              <option value="Mme">Mme</option>
            </select>
          </Champ>

          <Champ libelle="Prénom">
            <input
              className="champ"
              value={valeurs.prenomUsuel}
              onChange={(e) => setValeurs({ ...valeurs, prenomUsuel: e.target.value })}
              required
              autoFocus
            />
          </Champ>
        </div>

        <Champ libelle="Nom legal">
          <input
            className="champ"
            value={valeurs.nom}
            onChange={(e) => setValeurs({ ...valeurs, nom: e.target.value })}
          />
        </Champ>

        <Champ libelle="Email">
          <input
            className="champ"
            type="email"
            value={valeurs.email}
            onChange={(e) => setValeurs({ ...valeurs, email: e.target.value })}
          />
        </Champ>

        <ChampTelephone
          numero={valeurs.telephone}
          pays={valeurs.telephonePays}
          onChange={(telephone, telephonePays) => setValeurs({ ...valeurs, telephone, telephonePays })}
        />

        {peutPaie && (
          <fieldset className="space-y-4 rounded-md border border-encre-200 p-3">
            <legend className="px-1 text-xs font-medium uppercase tracking-wide text-encre-500">
              État civil et paiement — visible de la Direction seule
            </legend>

            <div className="grid grid-cols-2 gap-3">
              <Champ libelle="Date de naissance">
                <input
                  className="champ"
                  type="date"
                  value={valeurs.dateNaissance}
                  onChange={(e) => setValeurs({ ...valeurs, dateNaissance: e.target.value })}
                />
              </Champ>
              <Champ libelle="Lieu de naissance">
                <input
                  className="champ"
                  value={valeurs.lieuNaissance}
                  onChange={(e) => setValeurs({ ...valeurs, lieuNaissance: e.target.value })}
                />
              </Champ>
            </div>

            <Champ
              libelle="Numéro de Sécurité sociale"
              aide="13 ou 15 chiffres. Exigé par la déclaration d’embauche et le CEA."
            >
              <input
                className="champ tabulaire"
                inputMode="numeric"
                value={valeurs.numeroSecuriteSociale}
                onChange={(e) => setValeurs({ ...valeurs, numeroSecuriteSociale: e.target.value })}
                placeholder="—"
              />
            </Champ>

            <Champ libelle="Adresse">
              <input
                className="champ"
                value={valeurs.adresse}
                onChange={(e) => setValeurs({ ...valeurs, adresse: e.target.value })}
              />
            </Champ>
            <div className="grid grid-cols-3 gap-3">
              <Champ libelle="Code postal">
                <input
                  className="champ tabulaire"
                  inputMode="numeric"
                  value={valeurs.codePostal}
                  onChange={(e) => setValeurs({ ...valeurs, codePostal: e.target.value })}
                />
              </Champ>
              <div className="col-span-2">
                <Champ libelle="Ville">
                  <input
                    className="champ"
                    value={valeurs.ville}
                    onChange={(e) => setValeurs({ ...valeurs, ville: e.target.value })}
                  />
                </Champ>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Champ libelle="IBAN" aide="Le compte où part le salaire.">
                  <input
                    className="champ tabulaire"
                    value={valeurs.iban}
                    onChange={(e) => setValeurs({ ...valeurs, iban: e.target.value })}
                    placeholder="FR76 …"
                  />
                </Champ>
              </div>
              <Champ libelle="BIC">
                <input
                  className="champ tabulaire"
                  value={valeurs.bic}
                  onChange={(e) => setValeurs({ ...valeurs, bic: e.target.value })}
                />
              </Champ>
            </div>

            <Champ
              libelle="N° GUSO"
              aide="Guichet unique du spectacle occasionnel : à renseigner pour un salarié payé au cachet. Aucun format n’est imposé."
            >
              <input
                className="champ tabulaire"
                value={valeurs.numeroGuso}
                onChange={(e) => setValeurs({ ...valeurs, numeroGuso: e.target.value })}
              />
            </Champ>
          </fieldset>
        )}

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} />
      </form>
    </Modale>
  );
}

export function PiedFormulaire({
  erreur,
  enCours,
  onFermer,
  libelle = 'Enregistrer',
  enfants,
}: {
  erreur: string | null;
  enCours: boolean;
  onFermer: () => void;
  libelle?: string;
  enfants?: ReactNode;
}) {
  return (
    <>
      {erreur && (
        <p
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          role="alert"
        >
          {erreur}
        </p>
      )}
      {enfants}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="bouton-secondaire" onClick={onFermer}>
          Annuler
        </button>
        <button type="submit" className="bouton-principal" disabled={enCours}>
          {enCours ? 'Enregistrement…' : libelle}
        </button>
      </div>
    </>
  );
}
